/* eslint-disable react/jsx-props-no-spreading */
import React, { FC, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { FormProvider, useForm } from 'react-hook-form';
import { Modal } from '../../NominationGroups/style';
import { Col, FormRow } from 'src/UI/Grid';
import Button from 'src/UI/Button';
import FormControlTextInput from 'src/UI/FormComponent/FormControlTextInput';
import FormControlSelect from 'src/UI/FormComponent/FormControlSelect';
import FormControlDatePicker from 'src/UI/FormComponent/FormControlDatePicker';
import { post } from 'src/services/GenesisApi';
import { sendNotification } from 'src/UI/Notification';
import { MAX_LENGTH_20, MAX_LENGTH_50, MESSAGES, SET_MARKET } from 'src/constants/EUMarkets';
import { IAddEditMarket } from 'src/types/EUMarkets.type';
import { handleError } from 'src/utils/common/utils';
import { formatDateOnly, isDateRangeValid } from '../../Locations/AddEditParameter';

const defaultValues = {
  marketName: '',
  shortName: '',
  marketType: '',
  validFrom: dayjs(),
  validTo: null,
};

const marketTypeOptions = [
  { id: 'Country', name: 'Country' },
  { id: 'Pipeline', name: 'Pipeline' },
];

const AddEditMarket: FC<IAddEditMarket> = ({
  open = false,
  onClose = () => {},
  market = null,
  refreshData = () => {},
}) => {
  const methods = useForm();
  const { reset, setValue, watch } = methods;
  const [loading, setLoading] = useState<boolean>(false);
  const validFrom = watch?.('validFrom');
  const validTo = watch?.('validTo');

  useEffect(() => {
    if (market) {
      reset({
        marketName: market.marketName,
        shortName: market.shortName,
        marketType: market.marketType,
        validFrom: market.validFrom ? dayjs(market.validFrom) : undefined,
        validTo: market.validTo ? dayjs(market.validTo) : null,
      });
    } else {
      reset(defaultValues);
    }
  }, [market, open]);

  const handleClose = () => {
    reset(defaultValues);
    onClose();
  };

  const handleValidFromChange = (newValidFrom: Dayjs | null) => {
    if (newValidFrom && validTo && dayjs(newValidFrom).isAfter(dayjs(validTo), 'day')) {
      setValue('validTo', null, { shouldValidate: true });
    }
  };

  const handleOnSubmit = (formData) => {
    setLoading(true);
    const failureMessage = market ? MESSAGES.UPDATED_MARKET_FAILED : MESSAGES.CREATED_MARKET_FAILED;
    post(SET_MARKET, {
      id: market?.id ?? 0,
      marketName: formData.marketName,
      shortName: formData.shortName,
      marketType: formData.marketType,
      isActive: market?.isActive ?? true,
      validFrom: formatDateOnly(formData.validFrom as Dayjs),
      validTo: formData.validTo ? formatDateOnly(formData.validTo as Dayjs) : null,
    })
      .then(() => {
        sendNotification({
          type: 'success',
          message: market ? MESSAGES.UPDATED_MARKET : MESSAGES.CREATED_MARKET,
        });
        refreshData();
        handleClose();
      })
      .catch((error) => {
        handleError(error, failureMessage);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const renderFooter = () => (
    <div className='button-style'>
      <Button type='button' onClick={handleClose} variant='outlined' data-testid='btn-cancel'>
        Cancel
      </Button>
      <Button
        data-testid='submit-form'
        type='button'
        loading={loading}
        disabled={loading}
        onClick={methods.handleSubmit(handleOnSubmit)}
      >
        Save
      </Button>
    </div>
  );

  return (
    <Modal
      title={`${market ? 'Modify' : 'Add a new'} market`}
      open={open}
      onClose={handleClose}
      footer={renderFooter()}
      maskClosable={false}
      isDraggable
      width={800}
    >
      <FormProvider {...methods}>
        <FormRow>
          <Col size={6}>
            <FormControlTextInput
              controllerProps={{
                name: 'marketName',
                rules: {
                  required: MESSAGES.REQUIRED_MARKET_NAME,
                  maxLength: {
                    value: MAX_LENGTH_50,
                    message: MESSAGES.MAX_LENGTH_50,
                  },
                },
              }}
              label='Market Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Market Name' }}
            />
          </Col>
          <Col size={6}>
            <FormControlTextInput
              controllerProps={{
                name: 'shortName',
                rules: {
                  required: MESSAGES.REQUIRED_SHORT_NAME,
                  maxLength: {
                    value: MAX_LENGTH_20,
                    message: MESSAGES.MAX_LENGTH_20,
                  },
                },
              }}
              label='Short Name'
              methods={methods}
              inputProps={{ placeholder: 'Enter Short Name' }}
            />
          </Col>
        </FormRow>
        <FormRow>
          <Col size={4}>
            <FormControlSelect
              controllerProps={{
                name: 'marketType',
                rules: {
                  required: MESSAGES.REQUIRED_MARKET_TYPE,
                },
              }}
              label='Market Type'
              methods={methods}
              data={marketTypeOptions}
              isLoading={false}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid From'
              controllerProps={{
                name: 'validFrom',
                rules: {
                  required: MESSAGES.REQUIRED_VALID_FROM,
                  validate: (value) =>
                    isDateRangeValid(value, validTo) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              handleOnChange={handleValidFromChange}
            />
          </Col>
          <Col size={4}>
            <FormControlDatePicker
              methods={methods}
              label='Valid To'
              controllerProps={{
                name: 'validTo',
                rules: {
                  validate: (value) =>
                    isDateRangeValid(validFrom, value) || MESSAGES.INVALID_PARAMETER_DATE_RANGE,
                },
              }}
              inputProps={{
                disabledDate: (current: Dayjs) =>
                  validFrom ? current.isBefore(validFrom, 'day') : false,
              }}
            />
          </Col>
        </FormRow>
      </FormProvider>
    </Modal>
  );
};

export default AddEditMarket;
